import { api } from "@/lib/api";
import type { LeadDto, PageResponse, VisitDto } from "@/types/api";

export async function visitTrailForLead(leadId: number): Promise<VisitDto[]> {
    const { data } = await api.get<VisitDto[]>(`/admin/leads/${leadId}/visits`);
    return data;
}

export interface LeadFilters {
    domainId?: number | null;
    pageVersionId?: number | null;
    campaignId?: number | null;
    createdFrom?: string | null;
    createdTo?: string | null;
    preview?: boolean | null;
    emailContains?: string | null;
    phoneContains?: string | null;
    fields?: Record<string, string>;
    page?: number;
    size?: number;
    /** Advertiser filter (snapshot at lead time). -1 means unassigned. */
    advertiserId?: number | null;
    /** Affiliate filter (snapshot at lead time). -1 means unassigned. */
    affiliateId?: number | null;
}

function toParams(filters: LeadFilters): Record<string, string | number | boolean> {
    const params: Record<string, string | number | boolean> = {};
    if (filters.domainId != null) params.domainId = filters.domainId;
    if (filters.pageVersionId != null) params.pageVersionId = filters.pageVersionId;
    if (filters.campaignId != null) params.campaignId = filters.campaignId;
    if (filters.createdFrom) params.createdFrom = filters.createdFrom;
    if (filters.createdTo) params.createdTo = filters.createdTo;
    if (filters.preview != null) params.preview = filters.preview;
    if (filters.emailContains) params.emailContains = filters.emailContains;
    if (filters.phoneContains) params.phoneContains = filters.phoneContains;
    if (filters.page != null) params.page = filters.page;
    if (filters.size != null) params.size = filters.size;
    if (filters.advertiserId != null) params.advertiserId = filters.advertiserId;
    if (filters.affiliateId != null) params.affiliateId = filters.affiliateId;
    if (filters.fields) {
        for (const [k, v] of Object.entries(filters.fields)) {
            if (v) params[`field[${k}]`] = v;
        }
    }
    return params;
}

export async function searchLeads(filters: LeadFilters): Promise<PageResponse<LeadDto>> {
    const { data } = await api.get<PageResponse<LeadDto>>("/admin/leads", { params: toParams(filters) });
    return data;
}

export async function getLead(id: number): Promise<LeadDto> {
    const { data } = await api.get<LeadDto>(`/admin/leads/${id}`);
    return data;
}

export async function fieldsUnion(): Promise<string[]> {
    const { data } = await api.get<string[]>("/admin/leads/fields");
    return data;
}

/**
 * Delete a single lead (test/spam cleanup). The backend refuses with
 * 409 {error} when the lead has commission accruals (billing history).
 */
export async function deleteLead(id: number): Promise<void> {
    await api.delete(`/admin/leads/${id}`);
}

/**
 * Bulk-delete leads by id → {deleted: N}. Refused as a whole with
 * 409 {error} if ANY of the leads has commission accruals.
 */
export async function bulkDeleteLeads(ids: number[]): Promise<{ deleted: number }> {
    const { data } = await api.post<{ deleted: number }>("/admin/leads/bulk-delete", { ids });
    return data;
}

export function exportCsvUrl(filters: LeadFilters): string {
    const params = new URLSearchParams();
    const entries = Object.entries(toParams(filters));
    for (const [k, v] of entries) {
        params.set(k, String(v));
    }
    return `/admin/leads/export.csv?${params.toString()}`;
}
